import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Container,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Language as LanguageIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import api from '../utils/api';

const EmailVerificationPage = () => {
  const { t, i18n } = useTranslation('emailVerification');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState(i18n.language);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isResending, setIsResending] = useState(false);

  // Check if coming from a redirect with error parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const errorType = urlParams.get('error');
    const email = urlParams.get('email');

    if (email) {
      setEmail(decodeURIComponent(email));
    }

    if (errorType) {
      switch (errorType) {
        case 'verification_required':
          setError(t('request.messages.error'));
          break;
        case 'verification_disabled':
          setSuccess(t('request.messages.verificationDisabled'));
          setTimeout(() => navigate('/register'), 3000);
          break;
        default:
          break;
      }
    }
  }, [location.search, t, navigate]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return t('request.validation.emailRequired');
    }
    if (!emailRegex.test(email)) {
      return t('request.validation.emailInvalid');
    }
    if (email.length > 254) {
      return t('request.validation.emailTooLong');
    }
    return null;
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    // Clear previous errors
    setEmailError('');
    setError('');

    // Validate email in real-time
    const validationError = validateEmail(newEmail);
    if (validationError && newEmail.length > 0) {
      setEmailError(validationError);
    }
  };

  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value;
    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
  };

  const handleSubmit = async (e, isResend = false) => {
    e.preventDefault();

    // Validate email
    const validationError = validateEmail(email);
    if (validationError) {
      setEmailError(validationError);
      return;
    }

    setLoading(!isResend);
    setIsResending(isResend);
    setError('');
    setSuccess('');
    setEmailError('');

    try {
      const endpoint = isResend ? '/email-verification/resend' : '/email-verification/request';
      const response = await api.post(endpoint, {
        email: email.toLowerCase().trim(),
        language
      });

      if (response.data.success) {
        if (response.data.data?.verificationRequired === false) {
          setSuccess(response.data.message);
          setTimeout(() => navigate('/register'), 3000);
        } else {
          setSuccess(response.data.message);
          // Navigate to email sent page with the email
          setTimeout(() => {
            navigate('/email-verification/sent', {
              state: {
                email: email.toLowerCase().trim(),
                expiresAt: response.data.data?.expiresAt,
                requestCount: response.data.data?.requestCount
              }
            });
          }, 2000);
        }
      }
    } catch (err) {
      console.error('Email verification error:', err);

      if (err.response?.status === 429) {
        setError(t('request.messages.rateLimited'));
      } else if (err.response?.status === 409) {
        setError(t('request.messages.emailExists'));
      } else {
        setError(err.response?.data?.error || t('request.messages.error'));
      }
    } finally {
      setLoading(false);
      setIsResending(false);
    }
  };

  const handleResend = (e) => handleSubmit(e, true);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper
          elevation={8}
          sx={{
            p: isMobile ? 3 : 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)'
          }}
        >
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <EmailIcon
                sx={{
                  fontSize: 64,
                  color: 'primary.main',
                  mb: 2
                }}
              />
            </motion.div>

            <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
              {t('request.title')}
            </Typography>

            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              {t('request.subtitle')}
            </Typography>
          </Box>

          {/* Description */}
          <Box mb={4}>
            <Typography variant="body1" textAlign="center" color="text.secondary">
              {t('request.description')}
            </Typography>
          </Box>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <Box mb={3}>
              <TextField
                fullWidth
                label={t('request.form.email.label')}
                placeholder={t('request.form.email.placeholder')}
                type="email"
                value={email}
                onChange={handleEmailChange}
                error={!!emailError}
                helperText={emailError || t('request.form.email.helperText')}
                required
                autoFocus
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    )
                  }
                }}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth>
                <InputLabel>{t('request.form.language.label')}</InputLabel>
                <Select
                  value={language}
                  onChange={handleLanguageChange}
                  label={t('request.form.language.label')}
                  startAdornment={
                    <InputAdornment position="start">
                      <LanguageIcon color="action" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="en">
                    {t('request.form.language.options.en')}
                  </MenuItem>
                  <MenuItem value="tr">
                    {t('request.form.language.options.tr')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box display="flex" gap={2} justifyContent="center">
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || isResending || !!emailError}
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                sx={{ px: 4, py: 1.5 }}
              >
                {loading ? t('common.loading') : t('request.form.submit')}
              </Button>

              {email && (
                <Button
                  variant="outlined"
                  size="large"
                  disabled={loading || isResending || !!emailError}
                  startIcon={isResending ? <CircularProgress size={20} /> : <RefreshIcon />}
                  onClick={handleResend}
                  sx={{ px: 4, py: 1.5 }}
                >
                  {isResending ? t('common.loading') : t('request.form.resend')}
                </Button>
              )}
            </Box>
          </Box>

          {/* Information Cards */}
          <Box mt={4}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon color="info" />
                  {t('request.info.title')}
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('request.info.checkSpam')} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('request.info.expiryTime')} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('request.info.resendAvailable')} />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Box>

          {/* Footer */}
          <Divider sx={{ my: 3 }} />
          <Box textAlign="center">
            <Typography variant="body2" color="text.secondary">
              {t('request.footer')} <br />
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/login')}
                sx={{ mt: 1 }}
              >
                {t('request.backToLogin')}
              </Button>
            </Typography>
          </Box>
        </Paper>
      </motion.div>
    </Container>
  );
};

export default EmailVerificationPage;